import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTACT_EMAIL } from '../../lib/site-config.js';

// Lightweight 2-month side-by-side calendar with click-to-select date range.
// `unavailable` is an array of { start, end } from /api/ownerrez-availability.

// Format from the LOCAL date parts, never through toISOString(). Every Date in
// this component is built as local midnight (new Date(y, m, d)); serializing
// that through UTC shifts the day backwards for any guest east of UTC — a
// London visitor in summer picked "Aug 14" and sent "2026-08-13" to the quote
// and booking endpoints, and blocked-date matching was off by one too.
function ymd(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

function parseYmd(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, n) {
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function isDateInRanges(date, ranges) {
  const t = ymd(date);
  return ranges.some(({ start, end }) => t >= start && t < end);
}

const nightsBetween = (startStr, endStr) =>
  Math.round((parseYmd(endStr) - parseYmd(startStr)) / 86400000);

function MonthGrid({ month, today, range, unavailable, minNights, onPick, className = '' }) {
  const first = startOfMonth(month);
  const total = daysInMonth(month);
  const offset = first.getDay();
  const cells = [];

  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= total; d++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d));
  }

  const monthName = month.toLocaleDateString('en-US', { month: 'long' });
  const yearLabel = month.getFullYear();
  const hasFullRange = Boolean(range.start && range.end);

  return (
    <div className={['flex-1 min-w-0', className].join(' ')}>
      <div className="mb-2.5 flex items-baseline justify-center gap-2">
        <span className="font-display text-lg font-semibold text-navy-900">{monthName}</span>
        {/* navy-500, not gold, since the 2026-09-20 audit: gold marks structural kickers only. */}
        <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-navy-500">
          {yearLabel}
        </span>
      </div>
      <div className="grid grid-cols-7 text-center text-[10px] font-medium uppercase tracking-[0.18em] text-navy-500">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>
      {/* No horizontal gap: a selected range renders as one continuous band
          flowing through the cells, with rounded caps at the endpoints. */}
      <div className="mt-1.5 grid grid-cols-7 gap-y-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const t = ymd(d);
          const isPast = d < today;
          const blocked = isDateInRanges(d, unavailable);
          const isStart = range.start === t;
          const isEnd = range.end === t;
          const inRange =
            range.start && range.end && t > range.start && t < range.end;
          // Airbnb-style min-stay enforcement: while a check-out is being
          // chosen, dates that would make the stay too short aren't selectable.
          const tooClose =
            range.start && !range.end && t > range.start &&
            nightsBetween(range.start, t) < minNights;

          // Emphasis is deliberately inverted from the old grid (owner call,
          // 2026-07-28): open dates are confident ink, booked dates recede to
          // a faded strikethrough with no fill. The legend under the calendar
          // names the convention, which carries the meaning the lower-contrast
          // treatment alone would lose. navy-500/90, not navy-400/80: the
          // latter measured 2.77:1 on cream at 15px, the former 4.58:1, and it
          // still recedes behind the strikethrough (audit 2026-09-20).
          let band = 'relative aspect-square select-none';
          if (hasFullRange) {
            if (inRange) band += ' bg-navy-50';
            else if (isStart) band += ' rounded-l-full bg-navy-50';
            else if (isEnd) band += ' rounded-r-full bg-navy-50';
          }

          let btn =
            'absolute inset-0 flex items-center justify-center rounded-full text-[15px] transition-colors ';
          if (blocked) {
            btn += 'text-navy-500/90 line-through decoration-1 cursor-not-allowed';
          } else if (isPast) {
            btn += 'text-navy-200 cursor-not-allowed';
          } else if (tooClose) {
            btn += 'text-navy-300 cursor-not-allowed';
          } else if (isStart || isEnd) {
            btn += 'bg-navy-900 font-medium text-cream-50 shadow-sm cursor-pointer';
          } else if (inRange) {
            btn += 'text-navy-900 cursor-pointer';
          } else {
            btn +=
              'text-navy-800 hover:bg-cream-100 hover:ring-1 hover:ring-inset hover:ring-cream-400 cursor-pointer';
          }

          // Spoken date, not the raw ISO string: aria-label={t} made a screen
          // reader announce "two thousand twenty six dash zero eight dash..."
          const spoken = d.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          });
          let label = spoken;
          if (blocked) label = `${spoken}, unavailable`;
          else if (tooClose) label = `${spoken}, below the ${minNights}-night minimum`;
          else if (isStart) label = `${spoken}, selected check-in`;
          else if (isEnd) label = `${spoken}, selected check-out`;

          return (
            <div key={i} className={band}>
              <button
                type="button"
                disabled={isPast || blocked || tooClose}
                onClick={() => onPick(t)}
                className={btn}
                aria-label={label}
              >
                {d.getDate()}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// `initialRange` seeds a deep-linked stay (/stay/<slug>?checkin=&checkout=,
// the link an AI agent hands a guest). Read once at mount only: after that the
// guest's clicks own the selection. Stay.jsx has already validated it, so a
// bad range arrives here as nulls.
export default function AvailabilityCalendar({
  onRangeChange,
  minNights = 1,
  initialRange = null
}) {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Open on the deep-linked month, not this one: a stay eight months out would
  // otherwise land on a calendar showing no selection at all.
  const [cursorMonth, setCursorMonth] = useState(() =>
    startOfMonth(initialRange?.start ? parseYmd(initialRange.start) : today)
  );
  const [range, setRange] = useState(() => ({
    start: initialRange?.start || null,
    end: initialRange?.end || null
  }));
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // One silent retry before showing the degraded state, so a transient blip
  // doesn't take the whole booking module down.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const start = ymd(cursorMonth);
    const end = ymd(addMonths(cursorMonth, 3));
    let canceled = false;
    let retryTimer;
    setLoading(true);
    setFailed(false);
    fetch(`/api/ownerrez-availability?start=${start}&end=${end}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
      .then((data) => {
        if (canceled) return;
        setUnavailable(data.blocked || []);
        setLoading(false);
      })
      .catch(() => {
        if (canceled) return;
        if (attempt === 0) {
          retryTimer = setTimeout(() => setAttempt(1), 4000);
        } else {
          setFailed(true);
          setLoading(false);
        }
      });
    return () => {
      canceled = true;
      clearTimeout(retryTimer);
    };
  }, [cursorMonth, attempt]);

  // A deep-linked range can go stale between the moment an agent read
  // availability and the moment the guest opens the link. Once the real
  // calendar lands, drop a selection that now collides with a booking and say
  // so, rather than sending it to the quote for a 422.
  const pendingLinkCheck = useRef(Boolean(initialRange?.start && initialRange?.end));
  const [linkWentStale, setLinkWentStale] = useState(false);

  useEffect(() => {
    if (!pendingLinkCheck.current || loading || failed) return;
    pendingLinkCheck.current = false;
    if (!range.start || !range.end) return;
    const end = parseYmd(range.end);
    for (let cur = parseYmd(range.start); cur < end; cur.setDate(cur.getDate() + 1)) {
      if (isDateInRanges(cur, unavailable)) {
        const next = { start: null, end: null };
        setRange(next);
        onRangeChange?.(next);
        setLinkWentStale(true);
        return;
      }
    }
  }, [loading, failed, unavailable]);

  function pick(t) {
    if (!range.start || (range.start && range.end)) {
      const next = { start: t, end: null };
      setRange(next);
      onRangeChange?.(next);
    } else if (t <= range.start) {
      const next = { start: t, end: null };
      setRange(next);
      onRangeChange?.(next);
    } else if (nightsBetween(range.start, t) < minNights) {
      // Defensive: the cell is disabled in the grid, but never let a too-short
      // range through to the quote either way.
      return;
    } else {
      // Reject if range crosses an unavailable block.
      const startD = parseYmd(range.start);
      const endD = parseYmd(t);
      let cur = new Date(startD);
      while (cur <= endD) {
        if (isDateInRanges(cur, unavailable)) {
          // bail and reset start
          const next = { start: t, end: null };
          setRange(next);
          onRangeChange?.(next);
          return;
        }
        cur.setDate(cur.getDate() + 1);
      }
      const next = { start: range.start, end: t };
      setRange(next);
      onRangeChange?.(next);
    }
  }

  // Degraded state: never render a date grid without data behind it. With only
  // past dates crossed out, the legend would read as "every future date is
  // open" when nothing was actually checked.
  if (failed) {
    return (
      <div className="rounded-xl border border-navy-100 bg-cream-100/60 px-5 py-6">
        <p className="font-display text-base font-semibold text-navy-900">
          Our live calendar is briefly unavailable.
        </p>
        <p className="mt-2 text-sm text-navy-700">
          Email{' '}
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Booking dates`}
            className="font-medium text-navy-900 underline"
          >
            {CONTACT_EMAIL}
          </a>{' '}
          with your dates and we'll confirm availability and a full price within a
          few hours.
        </p>
        <button
          type="button"
          onClick={() => setAttempt((a) => a + 1)}
          className="mt-4 rounded-lg border border-navy-200 px-3 py-1.5 text-sm text-navy-700 hover:bg-cream-100"
        >
          Try the calendar again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Labelled icon buttons: these were bare "←" / "→" text glyphs, which a
          screen reader announces as "left arrow". min-h holds the row height so
          the loading string appearing and clearing cannot shift the grid. h-10,
          not h-8: the 32px arrows were the page's smallest tap targets. */}
      <div className="mb-3 flex min-h-[34px] items-center justify-between">
        <button
          type="button"
          onClick={() => setCursorMonth(addMonths(cursorMonth, -1))}
          aria-label="Previous month"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-200 text-navy-600 transition-colors hover:border-navy-900 hover:text-navy-900 disabled:opacity-40 disabled:hover:border-navy-200 disabled:hover:text-navy-600"
          disabled={cursorMonth <= startOfMonth(today)}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div aria-live="polite" className="font-serif text-sm italic text-navy-500">
          {loading ? 'Checking availability…' : ' '}
        </div>
        <button
          type="button"
          onClick={() => setCursorMonth(addMonths(cursorMonth, 1))}
          aria-label="Next month"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-navy-200 text-navy-600 transition-colors hover:border-navy-900 hover:text-navy-900"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {/* Lock the grid until availability has actually loaded, so the empty
          `unavailable` array during the fetch can't be misread as wide open. */}
      <div
        className={['flex gap-10', loading ? 'pointer-events-none opacity-40' : ''].join(' ')}
        aria-busy={loading}
      >
        <MonthGrid
          month={cursorMonth}
          today={today}
          range={range}
          unavailable={unavailable}
          minNights={minNights}
          onPick={pick}
        />
        {/* Below 480px two side-by-side months mean ~20px tap targets; one
            month full width keeps cells at 44px+. The arrows still page. */}
        <MonthGrid
          month={addMonths(cursorMonth, 1)}
          today={today}
          range={range}
          unavailable={unavailable}
          minNights={minNights}
          onPick={pick}
          className="hidden min-[480px]:block"
        />
      </div>
      {range.start && !range.end && minNights > 1 ? (
        <p className="mt-3 text-xs font-medium text-navy-700">
          {minNights}-night minimum: choose a check-out on or after{' '}
          {new Date(parseYmd(range.start).getTime() + minNights * 86400000)
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
        </p>
      ) : range.start && range.end ? (
        // Selection confirmed in the almanac's gold small caps — quiet
        // tactile feedback beside the grid; the dollar math lives in the
        // quote card.
        <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-cream-600">
          {parseYmd(range.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {' to '}
          {parseYmd(range.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {' · '}
          {nightsBetween(range.start, range.end)} nights
        </p>
      ) : linkWentStale ? (
        <p className="mt-3 text-xs font-medium text-navy-700">
          Those dates were taken while you were on your way here. Pick new ones below; struck-through
          dates are booked.
        </p>
      ) : (
        <p className="mt-3 text-xs text-navy-500">
          Select your check-in, then your check-out. Struck-through dates are booked.
        </p>
      )}
    </div>
  );
}
